import React, { useState, useEffect } from 'react';
import { useCurrentUser } from 'hooks/useCurrentUser';
import axios from 'axios';
import { normalizeJSONAPIResponse } from 'utils/modelUtils';

interface PartnerActivity {
  activeSeatsUsed: number;
  activeAdminSeatsUsed: number;
  activeSeatsUsedInPastYear: number;
  totalHistoricalUsers: number;
}

interface PartnerSeatsInfo {
  seatsOccupied: number;
  partnerActivity: PartnerActivity;
  partnerUmbrellaActivity: PartnerActivity;
}

export const SeatUsageInfo: React.FC = () => {
  const user = useCurrentUser();
  const partner = user?.primaryPartner;
  const isDepartment = partner.parent != null;
  const [seatInfo, setSeatInfo] = useState<PartnerSeatsInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!partner) return;

    const fetchSeatInfo = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`/api/v4/partners/${partner.id}/seats`);
        const newSeatInfo = normalizeJSONAPIResponse(data);
        setSeatInfo(newSeatInfo);
      } catch (error: any) {
        setError(error?.message || 'Error fetching seat info');
      } finally {
        setLoading(false);
      }
    };

    fetchSeatInfo();
  }, [partner]);

  if (!partner) {
    return <div className="p-4">No partner information available.</div>;
  }

  if (loading) {
    return <div className="p-4">Loading seat info...</div>;
  }

  if (error) {
    return <div className="p-4">Error loading seat info: {error}</div>;
  }

  if (!seatInfo) {
    return null;
  }

  const gridColumns = isDepartment ? 'md:grid-cols-2' : 'md:grid-cols-3';

  return (
    <div className={`grid gap-4 grid-cols-1 ${gridColumns} px-4`}>
      {/* Emphasized Seats Occupied Card (This is the number that matters for billing) */}
      <div className="bg-white shadow rounded-lg p-6 border-4 border-gray-500">
        <h2 className="text-2xl font-bold text-gray-600">Seats Occupied</h2>
        <p className="text-5xl font-extrabold text-gray-800 mt-4">{seatInfo.seatsOccupied.toLocaleString()}</p>
      </div>

      {/* Partner Activity Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-xl font-bold text-gray-700">Partner Info:</h2>
        <ul className="mt-4 space-y-2">
          <li>
            <span className="font-medium">Active Seats Used:</span>{' '}
            {seatInfo.partnerActivity.activeSeatsUsed.toLocaleString()}
          </li>
          <li>
            <span className="font-medium">Active Admin Seats:</span>{' '}
            {seatInfo.partnerActivity.activeAdminSeatsUsed.toLocaleString()}
          </li>
          <li>
            <span className="font-medium">Active Seats (Past Year):</span>{' '}
            {seatInfo.partnerActivity.activeSeatsUsedInPastYear.toLocaleString()}
          </li>
          <li>
            <span className="font-medium">Total Historical Users:</span>{' '}
            {seatInfo.partnerActivity.totalHistoricalUsers.toLocaleString()}
          </li>
        </ul>
      </div>

      {/* Partner Umbrella Activity Card */}
      {!isDepartment && (
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-bold text-gray-700">Partner+Departments Info:</h2>
          <ul className="mt-4 space-y-2">
            <li>
              <span className="font-medium">Active Seats Used:</span>{' '}
              {seatInfo.partnerUmbrellaActivity.activeSeatsUsed.toLocaleString()}
            </li>
            <li>
              <span className="font-medium">Active Admin Seats:</span>{' '}
              {seatInfo.partnerUmbrellaActivity.activeAdminSeatsUsed.toLocaleString()}
            </li>
            <li>
              <span className="font-medium">Active Seats (Past Year):</span>{' '}
              {seatInfo.partnerUmbrellaActivity.activeSeatsUsedInPastYear.toLocaleString()}
            </li>
            <li>
              <span className="font-medium">Total Historical Users:</span>{' '}
              {seatInfo.partnerUmbrellaActivity.totalHistoricalUsers.toLocaleString()}
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};
